import type { ArealplanDelområde } from '@origo/arealplan';
import { OBJTYPE_REGISTRY } from '@origo/arealplan';

/**
 * Mapping OBJTYPE → [headingFelt, typeFelt]. Avledet fra det sentrale
 * `OBJTYPE_REGISTRY` for delområde-typer; admin-spesifikke planområde-typer
 * (RpOmråde / KpOmråde) legges på lokalt siden de ikke er delområder og
 * derfor ikke hører hjemme i delområde-registeret.
 */
export const objectTypeAttributesMappings: Record<string, string[]> = {
    ...Object.fromEntries(
        Object.values(OBJTYPE_REGISTRY).map((info) => [
            info.canonical,
            [info.headingFelt, info.typeFelt],
        ])
    ),
    "RpOmråde": ["PLANID", "PLANNAVN", "PLANTYPE", "PLANSTAT", "IKRAFT"],
    "KpOmråde": ["PLANID", "PLANNAVN", "PLANTYPE", "PLANSTAT", "IKRAFT"],
};

/**
 * Hent visning-overskriften for et arealformål-område. Foretrekker
 * type-felt-display-verdi (f.eks. "Bolig") når tilgjengelig, fallback til
 * heading-felt eller OBJTYPE.
 */
export const getFeatureHeading = (
    feature: ArealplanDelområde,
    mappings: Record<string, string[]>
): string => {
    const objtype = feature.objtype ?? '';
    const fields = mappings[objtype] || ["OBJTYPE"];
    const typeDisplay = feature.displayValues?.[fields[1]];
    if (typeDisplay != null) return String(typeDisplay);
    const heading = feature.attributter?.[fields[0]];
    return heading != null ? String(heading) : objtype;
};

/**
 * Hent beskrivelse for et arealformål-område — typisk feltnavn-verdi.
 */
export const getFeatureDescription = (
    feature: ArealplanDelområde,
    mappings: Record<string, string[]>
): string => {
    const objtype = feature.objtype ?? '';
    const fields = mappings[objtype] || ["OBJTYPE"];
    const headingDisplay = feature.displayValues?.[fields[0]];
    if (headingDisplay != null) return String(headingDisplay);
    const heading = feature.attributter?.[fields[0]];
    return heading != null ? String(heading) : '';
};

const arealFmt = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });

/** Formaterer areal i m² med norsk tusenskille, f.eks. "1 200 m²". */
export const formatAreal = (m2?: number): string | undefined =>
    m2 == null || !Number.isFinite(m2) ? undefined : `${arealFmt.format(Math.round(m2))} m²`;
